import { TravelAlertTypesID } from "@shared/constants/travel-alert-types.enum";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import {
  TRAVEL_CYCLE_OUTCOME_GROUP,
  TravelCycleOutcomeGroups,
  TravelCycleOutcomes
} from "@shared/constants/travel-cycle-causes.enum";
import { TravelCycleHardwareKind } from "@shared/interfaces/travel-cycle-analysis.interface";

/**
 * Colors for the 7 alert types in the horizontal bar chart.
 * Order matches the seed in `tpe_travel_analysis_alert_types`.
 */
export const ALERT_TYPE_COLORS: string[] = [
  "#ff6c5c", // Ciclo transversal (D+1)
  "#ff9800", // Déficit de comandos de hardware
  "#ffc107", // Déficit de comandos de sensor (IDLE elevado)
  "#e91e63", // Falha de posicionamento GPS
  "#9c27b0", // Inconsistência de fluxo logístico
  "#3f51b5", // Informações de CAN travadas
  "#00bcd4", // Instabilidade de conexão GPRS
  "#f44336"  // Falsos positivos de descarga
];

/** A single alert type entry rendered in the help glossary drawer. */
export interface AlertGlossaryEntry {
  /** Alert type UUID (matches TravelAlertTypesID). */
  id: string;
  /** i18n key for the alert title. */
  titleKey: TravelAnalysisMessages;
  /** i18n key for the "what it detects" description. */
  descriptionKey: TravelAnalysisMessages;
  /** i18n key for the "when it fires" condition. */
  whenKey: TravelAnalysisMessages;
  /** i18n key for the affected vehicle scope. */
  appliesKey: TravelAnalysisMessages;
  /** i18n key for the hint on how to read the metadata. */
  metadataHintKey: TravelAnalysisMessages;
  /** Existing metadata label i18n keys captured by this alert, shown as chips. */
  metadataKeys: TravelAnalysisMessages[];
}

/**
 * Static description of every travel-analysis alert type, shown in the help
 * glossary regardless of which alerts appear in the current dashboard period.
 * Order: alerts that apply to all vehicles first, then mixer/pump-specific ones.
 */
export const ALERT_GLOSSARY: AlertGlossaryEntry[] = [
  {
    id: TravelAlertTypesID.INSTABILIDADE_GPRS,
    titleKey: TravelAnalysisMessages.glossaryGprsTitle,
    descriptionKey: TravelAnalysisMessages.glossaryGprsDesc,
    whenKey: TravelAnalysisMessages.glossaryGprsWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesAll,
    metadataHintKey: TravelAnalysisMessages.glossaryGprsMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaTotalPackets,
      TravelAnalysisMessages.metaRealtimeCommands,
      TravelAnalysisMessages.metaMemoryCommands,
      TravelAnalysisMessages.metaEffectiveRealtimeCommands,
      TravelAnalysisMessages.metaMemoryRatio,
      TravelAnalysisMessages.metaThresholdPercent,
      TravelAnalysisMessages.metaEffectiveRealtimeThresholdSeconds,
      TravelAnalysisMessages.metaAvgDelaySeconds,
      TravelAnalysisMessages.metaMaxDelaySeconds
    ]
  },
  {
    id: TravelAlertTypesID.FALHA_POSICIONAMENTO_GPS,
    titleKey: TravelAnalysisMessages.glossaryGpsTitle,
    descriptionKey: TravelAnalysisMessages.glossaryGpsDesc,
    whenKey: TravelAnalysisMessages.glossaryGpsWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesAll,
    metadataHintKey: TravelAnalysisMessages.glossaryGpsMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaTotalGpsPackets,
      TravelAnalysisMessages.metaInvalidFixCount,
      TravelAnalysisMessages.metaInvalidRatio,
      TravelAnalysisMessages.metaThresholdPercent,
      TravelAnalysisMessages.metaAvgSatellites
    ]
  },
  {
    id: TravelAlertTypesID.CICLO_TRANSVERSAL,
    titleKey: TravelAnalysisMessages.glossaryCrossDayTitle,
    descriptionKey: TravelAnalysisMessages.glossaryCrossDayDesc,
    whenKey: TravelAnalysisMessages.glossaryCrossDayWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixerPump,
    metadataHintKey: TravelAnalysisMessages.glossaryCrossDayMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaStartDate,
      TravelAnalysisMessages.metaFinishDate,
      TravelAnalysisMessages.metaStartDay,
      TravelAnalysisMessages.metaFinishDay,
      TravelAnalysisMessages.metaDurationHours
    ]
  },
  {
    id: TravelAlertTypesID.DEFICIT_COMANDOS_HARDWARE,
    titleKey: TravelAnalysisMessages.glossaryHwDeficitTitle,
    descriptionKey: TravelAnalysisMessages.glossaryHwDeficitDesc,
    whenKey: TravelAnalysisMessages.glossaryHwDeficitWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixerPump,
    metadataHintKey: TravelAnalysisMessages.glossaryHwDeficitMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaPacketCount,
      TravelAnalysisMessages.metaExpectedPackets,
      TravelAnalysisMessages.metaMinimumRequired,
      TravelAnalysisMessages.metaDeficitPercent,
      TravelAnalysisMessages.metaRatePerMinute,
      TravelAnalysisMessages.metaTravelDurationMinutes,
      TravelAnalysisMessages.metaThresholdPercent
    ]
  },
  {
    id: TravelAlertTypesID.DEFICIT_COMANDOS_SENSOR,
    titleKey: TravelAnalysisMessages.glossarySensorIdleTitle,
    descriptionKey: TravelAnalysisMessages.glossarySensorIdleDesc,
    whenKey: TravelAnalysisMessages.glossarySensorIdleWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixer,
    metadataHintKey: TravelAnalysisMessages.glossarySensorIdleMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaTotalPackets,
      TravelAnalysisMessages.metaIgnitionOnCount,
      TravelAnalysisMessages.metaIdleCount,
      TravelAnalysisMessages.metaLoadingCount,
      TravelAnalysisMessages.metaUnloadingCount,
      TravelAnalysisMessages.metaIdleRatio,
      TravelAnalysisMessages.metaThresholdPercent
    ]
  },
  {
    id: TravelAlertTypesID.INCONSISTENCIA_FLUXO_LOGISTICO,
    titleKey: TravelAnalysisMessages.glossaryLogisticsTitle,
    descriptionKey: TravelAnalysisMessages.glossaryLogisticsDesc,
    whenKey: TravelAnalysisMessages.glossaryLogisticsWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixerDoc,
    metadataHintKey: TravelAnalysisMessages.glossaryLogisticsMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaRequiredStates,
      TravelAnalysisMessages.metaActualStates,
      TravelAnalysisMessages.metaMissingStates,
      TravelAnalysisMessages.metaMissingCount,
      TravelAnalysisMessages.metaTotalStates,
      TravelAnalysisMessages.metaNumDoc
    ]
  },
  {
    id: TravelAlertTypesID.CAN_TRAVADO,
    titleKey: TravelAnalysisMessages.glossaryCanStallTitle,
    descriptionKey: TravelAnalysisMessages.glossaryCanStallDesc,
    whenKey: TravelAnalysisMessages.glossaryCanStallWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixer,
    metadataHintKey: TravelAnalysisMessages.glossaryCanStallMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaSource,
      TravelAnalysisMessages.metaStallType,
      TravelAnalysisMessages.metaThreshold,
      TravelAnalysisMessages.metaStalledFields,
      TravelAnalysisMessages.metaTotalCanPackets
    ]
  },
  {
    id: TravelAlertTypesID.FALSO_POSITIVO_DESCARGA,
    titleKey: TravelAnalysisMessages.glossaryFalseDischargeTitle,
    descriptionKey: TravelAnalysisMessages.glossaryFalseDischargeDesc,
    whenKey: TravelAnalysisMessages.glossaryFalseDischargeWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixer,
    metadataHintKey: TravelAnalysisMessages.glossaryFalseDischargeMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaTotalDischargeCommands,
      TravelAnalysisMessages.metaCoveredCommands,
      TravelAnalysisMessages.metaOrphanCommands,
      TravelAnalysisMessages.metaOrphanRatio,
      TravelAnalysisMessages.metaCoveredRanges,
      TravelAnalysisMessages.metaThreshold
    ]
  }
];

/** Default filter period in days when no date range is selected. */
export const DEFAULT_PERIOD_DAYS = 30;

/** Color of the complete cycle in the impact bar and the "on target" rates. */
export const CYCLE_COMPLETE_COLOR = "#4caf50";

/** Rates at or above the target are green, down to this one yellow, below it red. */
export const CYCLE_WARNING_RATE = 75;

/** Status colors of a complete-cycle rate. */
export const CYCLE_RATE_COLORS = { ok: "#4caf50", warning: "#f0a202", critical: "#ff6c5c", muted: "#999999" };

/** A regional or plant with fewer travels than this shows its rate in gray, marked "few travels". */
export const MIN_PLACE_TRAVELS = 30;

/** A day with fewer travels than this is left out of a line of the daily chart. */
export const MIN_DAILY_TRAVELS = 5;

/** Plants drawn in the daily chart without a regional or plant filter (the lowest rates). */
export const CHART_WORST_PLANTS = 8;

/** Plants drawn in the daily chart with a regional or plant filter. */
export const CHART_FILTERED_PLANTS = 10;

/** Line colors of the daily chart, one per regional or plant. */
export const PLACE_CHART_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#c98500", "#8e5bd8", "#d1447f", "#0e9bb0", "#6f7f12", "#5b6b7d", "#b5523b"];

/** One group of failure outcomes: where to act. */
export interface CycleGroupEntry {
  key: TravelCycleOutcomeGroups;
  nameKey: TravelAnalysisMessages;
  descriptionKey: TravelAnalysisMessages;
  color: string;
}

/** The four groups of failure outcomes, in the order of the impact bar. */
export const CYCLE_GROUPS: CycleGroupEntry[] = [
  {
    key: TravelCycleOutcomeGroups.EQUIPMENT,
    nameKey: TravelAnalysisMessages.groupEquipment,
    descriptionKey: TravelAnalysisMessages.groupEquipmentDesc,
    color: "#eb6834"
  },
  {
    key: TravelCycleOutcomeGroups.REGISTRATION,
    nameKey: TravelAnalysisMessages.groupRegistration,
    descriptionKey: TravelAnalysisMessages.groupRegistrationDesc,
    color: "#2a78d6"
  },
  {
    key: TravelCycleOutcomeGroups.ROUTINE,
    nameKey: TravelAnalysisMessages.groupRoutine,
    descriptionKey: TravelAnalysisMessages.groupRoutineDesc,
    color: "#8e5bd8"
  },
  {
    key: TravelCycleOutcomeGroups.OTHERS,
    nameKey: TravelAnalysisMessages.groupOthers,
    descriptionKey: TravelAnalysisMessages.groupOthersDesc,
    color: "#0e9bb0"
  }
];

/** Texts of one failure outcome. */
export interface CycleOutcomeEntry {
  outcome: CycleFailureOutcome;
  nameKey: TravelAnalysisMessages;
  descriptionKey: TravelAnalysisMessages;
  actionKey: TravelAnalysisMessages;
}

/** Every outcome but the complete cycle. */
export type CycleFailureOutcome = Exclude<TravelCycleOutcomes, TravelCycleOutcomes.COMPLETE>;

/** Failure outcomes in the glossary order, grouped as in CYCLE_GROUPS. */
export const CYCLE_OUTCOMES: CycleOutcomeEntry[] = [
  [TravelCycleOutcomes.SENSOR_STOPPED, "SensorStopped"],
  [TravelCycleOutcomes.SENSOR_INTERMITTENT, "SensorIntermittent"],
  [TravelCycleOutcomes.DISCHARGE_ONE_OFF, "DischargeOneOff"],
  [TravelCycleOutcomes.TRACKER_WITHOUT_SIGNAL, "TrackerWithoutSignal"],
  [TravelCycleOutcomes.INACTIVE_PLANT, "InactivePlant"],
  [TravelCycleOutcomes.JOBSITE_NOT_DETECTED, "JobsiteNotDetected"],
  [TravelCycleOutcomes.DISCHARGE_OUTSIDE_JOBSITE, "DischargeOutsideJobsite"],
  [TravelCycleOutcomes.JOBSITE_NEXT_TO_PLANT, "JobsiteNextToPlant"],
  [TravelCycleOutcomes.SHORT_PLANT_EXIT, "ShortPlantExit"],
  [TravelCycleOutcomes.REPLACED_TICKET, "ReplacedTicket"],
  [TravelCycleOutcomes.TWO_TICKETS, "TwoTickets"],
  [TravelCycleOutcomes.INTEGRATION_NOT_SENT, "IntegrationNotSent"],
  [TravelCycleOutcomes.OTHER, "Other"]
].map(([outcome, name]) => ({
  outcome: outcome as CycleFailureOutcome,
  nameKey: TravelAnalysisMessages[`outcome${name}` as keyof typeof TravelAnalysisMessages],
  descriptionKey: TravelAnalysisMessages[`outcome${name}Desc` as keyof typeof TravelAnalysisMessages],
  actionKey: TravelAnalysisMessages[`outcome${name}Action` as keyof typeof TravelAnalysisMessages]
}));

/** Name of the group of a failure outcome, by its key. */
export const CYCLE_GROUP_OF = CYCLE_OUTCOMES.reduce((byOutcome, { outcome }) => ({
  ...byOutcome,
  [outcome]: CYCLE_GROUPS.find((g) => g.key === TRAVEL_CYCLE_OUTCOME_GROUP[outcome])
}), {} as Record<CycleFailureOutcome, CycleGroupEntry>);

/** Labels of the 5 cycle states, in the order of `registeredStates`. */
export const CYCLE_STATE_LABELS: TravelAnalysisMessages[] = [
  TravelAnalysisMessages.stateOnTheWay,
  TravelAnalysisMessages.stateJobsite,
  TravelAnalysisMessages.stateDischarging,
  TravelAnalysisMessages.stateReturning,
  TravelAnalysisMessages.statePlant
];

/** Label and colors of each kind of hardware to check. */
export const HARDWARE_KINDS: Record<TravelCycleHardwareKind, { labelKey: TravelAnalysisMessages; background: string; color: string }> = {
  sensor_stopped: { labelKey: TravelAnalysisMessages.hwSensorStopped, background: "#fdecec", color: "#b03030" },
  sensor_intermittent: { labelKey: TravelAnalysisMessages.hwSensorIntermittent, background: "#fdf3e1", color: "#8a5a00" },
  tracker_without_signal: { labelKey: TravelAnalysisMessages.hwTracker, background: "#e8eef7", color: "#2c4f7c" }
};
